import {EditorView} from '@codemirror/view'
import type {ImageDimensions} from '@github-ui/image-utils'
import {getImageSizeFromFile, RETINA_PPI} from '@github-ui/image-utils'
import {noop} from '@github-ui/noop'
import {useSafeAsyncCallback} from '@github-ui/use-safe-async-callback'
import {useTrackingRef} from '@github-ui/use-tracking-ref'
import type {FileType, UnifiedFileSelectResult} from '@github-ui/use-unified-file-select'
import {useUnifiedFileSelect} from '@github-ui/use-unified-file-select'
import type React from 'react'
import {type MouseEvent, useCallback, useEffect, useRef, useState} from 'react'

import {clearDropPosition, getDropPosition} from '../codemirror/extensions/drop-position'
import {
  addUploadPlaceholders,
  replaceUploadPlaceholder,
  updateUploadPlaceholder,
} from '../codemirror/extensions/upload-placeholder'
import {htmlImage} from '../language-tools/html/builders'
import {markdownImage, markdownLink} from '../language-tools/markdown/builders'
import type {HybridEditorTelemetry} from '../telemetry'

/** List of image MIME types which we do not attempt to render as images. */
const excludedImageMimeTypes = new Set(['image/bmp', 'image/tiff', 'image/heic', 'image/heif'])

const isImageFile = (file: File) =>
  file.type.toLowerCase().startsWith('image/') && !excludedImageMimeTypes.has(file.type.toLowerCase())
const isVideoFile = (file: File) => file.type.toLowerCase().startsWith('video/')

const normalizeUploadUrl = (url: string) => {
  const trimmed = url.trim()
  if (!trimmed) return trimmed

  if (typeof window === 'undefined' || !window.location?.origin) return trimmed

  try {
    return new URL(trimmed, window.location.origin).href
  } catch {
    return trimmed
  }
}

function calculateRenderedImageDimensions(originalDimensions: ImageDimensions) {
  if (originalDimensions.pixelsPerInch === RETINA_PPI) {
    return {
      width: Math.round(originalDimensions.width / 2),
      height: Math.round(originalDimensions.height / 2),
    }
  }

  return undefined // for non-retina, auto size to the original dimensions
}

const sizedMarkdownImage = (src: string, alt: string, dimensions?: Dimensions) => {
  return dimensions ? htmlImage(alt, src, dimensions.width, dimensions.height) : markdownImage(alt, src)
}

const markdown = (entry: PreparedUploadEntry, result: FileUploadResult) => {
  switch (entry.mediaKind) {
    case 'video':
      return result.url
    case 'image':
      return sizedMarkdownImage(result.url, 'Image', entry.dimensions)
    case undefined:
      return markdownLink(result.file.name, result.url)
  }
}

interface FileHandler extends UnifiedFileSelectResult {
  errorMessage?: React.ReactNode
}

interface UseFileHandlingProps {
  repositoryId?: number
  view: EditorView | null
  disabled?: boolean
  value: string
  onUploadFile?: (file: File, onProgress?: (progressBytes: number) => void) => Promise<FileUploadResult>
  acceptedFileTypes?: FileType[]
  onFilesRejected?: (files: File[], rejectedTypes: string[]) => void
}

export interface FileUploadResult {
  /** The URL of the uploaded file. `null` if the upload failed (or reject the promise). */
  url: string
  /**
   * The file that was uploaded. Typically the client-side detected name, size, and content
   * type can be unreliable, so your file upload service may provide more accurate data. By
   * receiving an updated File instance with the more accurate data, the Markdown editor can
   * make better decisions.
   */
  file: File
}

type OptFileUploadResult = FileUploadResult | {url: null; file: File; error?: Error}

interface Dimensions {
  width: number
  height: number
}

interface PreparedUploadEntry {
  id: string
  file: File
  previewUrl?: string
  mediaKind?: 'image' | 'video'
  dimensions?: Dimensions
}

export const useFileHandler = (
  {value, view, disabled, onUploadFile, acceptedFileTypes, onFilesRejected}: UseFileHandlingProps,
  telemetry: HybridEditorTelemetry,
): FileHandler | null => {
  const [errorMessage, setErrorMessage] = useState<React.ReactNode | undefined>(undefined)
  const placeholderIdRef = useRef(0)

  const viewRef = useTrackingRef(view)

  const errorVisibleForEnoughTimeRef = useRef(false)
  useEffect(() => {
    if (errorMessage) {
      errorVisibleForEnoughTimeRef.current = false
      const id = setTimeout(() => (errorVisibleForEnoughTimeRef.current = true), 1000)
      return () => clearTimeout(id)
    }
  }, [errorMessage])

  useEffect(() => {
    // clears the error message when the user types and enough time has passed

    if (errorVisibleForEnoughTimeRef.current) setErrorMessage(undefined)
  }, [value])

  const safeSetRejectedFiles = useSafeAsyncCallback((files: File[]) => {
    const types = new Set(
      files
        .map(({name}) => {
          const parts = name.split('.')
          return parts.length > 1 ? `.${parts.at(-1)}` : ''
        })
        .filter(s => s !== ''),
    )

    if (types.size > 0) {
      const typesList = [...types].join(', ')
      setErrorMessage(
        <>
          <span>
            File type<span>{types.size > 1 ? 's' : ''}</span> {typesList} not supported. See the{' '}
          </span>
          <a href="https://docs.github.com/en/get-started/writing-on-github/working-with-advanced-formatting/attaching-files">
            documentation
          </a>
          <span> for supported file types.</span>
        </>,
      )
      onFilesRejected?.(files, [...types])
    }
  })

  const updatePlaceholderProgress = useCallback(
    (entryId: string, progressPercent: number | undefined) => {
      if (viewRef.current) updateUploadPlaceholder(viewRef.current, {id: entryId, data: {progressPercent}})
    },
    [viewRef],
  )

  const insertPlaceholders = useCallback(
    (entries: PreparedUploadEntry[]) => {
      const editorView = viewRef.current
      if (!editorView || entries.length === 0) return

      // Determine where to insert the placeholder:
      // 1. Check if there's a drop position from a recent drop event (user dragged files)
      // 2. Fall back to the current selection start (user clicked upload button)
      let insertPos = getDropPosition(editorView.state)
      if (insertPos !== null) {
        // Clear the drop position so it's not reused for subsequent operations
        clearDropPosition(editorView)
      } else {
        // Fall back to the current selection start
        insertPos = editorView.state.selection.main.from
      }

      editorView.focus()

      addUploadPlaceholders(
        editorView,
        insertPos,
        ...entries.map(entry => ({
          id: entry.id,
          data: {
            fileName: entry.file.name,
            progressPercent: 0,
            previewUrl: entry.previewUrl,
            mediaKind: entry.mediaKind,
            dimensions: entry.dimensions,
          },
        })),
      )
    },
    [viewRef],
  )

  // It's crucial that this is done safely because file uploads can take a long time - there's
  // a very good chance that the references will be outdated or the component unmounted by the time this is called.
  const safeHandleCompletedFileUpload = useSafeAsyncCallback(
    (entry: PreparedUploadEntry, result: OptFileUploadResult) => {
      const editorView = viewRef.current
      if (!editorView) return

      // even if there was an error, we still need to remove the placeholder
      const replacement = result.url ? markdown(entry, result) : ''

      replaceUploadPlaceholder(editorView, entry.id, replacement)

      if (!replacement) {
        setErrorMessage(
          'error' in result && result.error !== undefined
            ? `File upload failed: ${result.error.message}`
            : 'File upload failed',
        )
      }

      // Clean up blob URL
      if (entry.previewUrl) {
        URL.revokeObjectURL(entry.previewUrl)
      }
    },
  )

  const uploadFiles = useCallback(
    (entries: PreparedUploadEntry[]): Array<Promise<void>> =>
      entries.map(async entry => {
        // Provide a progress callback for the uploader to track upload progress
        const progressCallback = (progressBytes: number) => {
          const percent = entry.file.size > 0 ? Math.round((progressBytes / entry.file.size) * 100) : 0
          updatePlaceholderProgress(entry.id, Math.min(percent, 99)) // Cap at 99% until complete
        }

        let result: OptFileUploadResult = {url: null, file: entry.file}
        try {
          result = (await onUploadFile?.(entry.file, progressCallback)) ?? {file: entry.file, url: null}
        } catch (error) {
          result = {file: entry.file, url: null, error: error instanceof Error ? error : undefined}

          // rethrow asynchronously so the error is reported and logged
          setTimeout(() => {
            throw error
          })
        }

        result.url &&= normalizeUploadUrl(result.url)

        telemetry?.trackFileUpload({
          phase: result.url ? 'succeeded' : 'failed',
          mimeType: entry.file.type || 'unknown',
          sizeBytes: entry.file.size,
          mediaKind: entry.mediaKind ?? 'other',
          errorType: result.url ? undefined : 'upload',
        })

        safeHandleCompletedFileUpload(entry, result)
      }),
    [onUploadFile, safeHandleCompletedFileUpload, telemetry, updatePlaceholderProgress],
  )

  const onSelectFiles = useCallback(
    async (accepted: File[], rejected: File[]) => {
      if (accepted.length > 0) {
        const total = accepted.length
        const entries: PreparedUploadEntry[] = await Promise.all(
          accepted.map(async (file, index) => {
            const id = `upload-placeholder-${placeholderIdRef.current++}`
            const mediaKind = isVideoFile(file) ? 'video' : isImageFile(file) ? 'image' : undefined
            const previewUrl = mediaKind ? URL.createObjectURL(file) : undefined

            let originalDimensions: ImageDimensions | null = null
            if (mediaKind === 'image') {
              try {
                originalDimensions = await getImageSizeFromFile(file)
              } catch (error) {
                setTimeout(() => {
                  throw error
                })
              }
            }

            return {
              id,
              file,
              order: index + 1,
              total,
              previewUrl,
              mediaKind,
              dimensions: originalDimensions ? calculateRenderedImageDimensions(originalDimensions) : undefined,
            }
          }),
        )

        for (const entry of entries) {
          telemetry?.trackFileUpload({
            phase: 'selected',
            mimeType: entry.file.type || 'unknown',
            sizeBytes: entry.file.size,
            mediaKind: entry.mediaKind ?? 'other',
          })
        }

        insertPlaceholders(entries)

        await Promise.allSettled(uploadFiles(entries))
      }
      // setting rejected files will hide upload progress, replacing it with an error message
      // so only call it after successful files are uploaded
      safeSetRejectedFiles(rejected)
    },
    [safeSetRejectedFiles, insertPlaceholders, telemetry, uploadFiles],
  )

  const fileSelect = useUnifiedFileSelect({
    acceptedFileTypes,
    multi: true,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    onSelect: onSelectFiles,
  })

  if (disabled || !view) {
    return {
      clickTargetProps: {
        onClick: noop,
      },
      dropTargetProps: {
        onDragEnter: noop,
        onDragLeave: noop,
        onDrop: noop,
        onDragOver: noop,
      },
      pasteTargetProps: {
        onPaste: noop,
      },
      isDraggedOver: false,
    }
  }

  // Add telemetry to click target
  const clickTargetProps = {
    ...fileSelect.clickTargetProps,
    onClick: (event: MouseEvent<HTMLButtonElement>) => {
      telemetry?.trackToolbarInteraction({
        control: 'upload_file',
        action: 'upload_file',
        trigger: 'toolbar',
        isActive: false,
        handled: true,
      })

      fileSelect.clickTargetProps.onClick(event)
    },
  }

  // Fix dragleave issue when dragging over child elements
  const dropTargetProps = {
    ...fileSelect.dropTargetProps,
    onDragLeave: (event: React.DragEvent<HTMLDivElement>) => {
      // Only call the original handler if the drag is truly leaving the container
      // Check if the relatedTarget (where the drag is moving to) is outside the container
      if (!(event.currentTarget as Node).contains(event.relatedTarget as Node)) {
        fileSelect.dropTargetProps.onDragLeave(event as unknown as React.DragEvent<HTMLDivElement>)
      }
    },
  }

  return onUploadFile
    ? {
        ...fileSelect,
        clickTargetProps,
        dropTargetProps,
        errorMessage,
      }
    : null
}
